import React, { useEffect, useRef, useState } from 'react';
import {
  ChevronDown,
  CircleHelp,
  LogOut,
  UserRound
} from 'lucide-react';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';

const formatRole = (role) => {
  const normalized = String(role || '').trim().toLowerCase();
  if (!normalized) return 'Customer account';
  return `Organization ${normalized}`;
};

const UserMenu = ({ onLogout, role }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const username = Cookies.get('username') || 'Unknown User';
  const email = Cookies.get('email') || 'No Email';
  const initial = username.charAt(0).toUpperCase();
  const roleLabel = formatRole(role);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProfileSettings = () => {
    setIsOpen(false);
    navigate('/settings');
  };

  const handleSupportSettings = () => {
    setIsOpen(false);
    navigate('/support');
  };

  const handleLogout = () => {
    setIsOpen(false);
    onLogout();
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className={`group mt-0 flex h-10 min-w-[175px] items-center gap-2.5 rounded-xl border bg-white px-2.5 pr-3 text-left shadow-sm transition-all duration-200 hover:border-slate-300 hover:shadow-md ${
          isOpen ? 'border-cyan-200 ring-4 ring-cyan-50' : 'border-slate-200'
        }`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 text-sm font-semibold text-slate-600 ring-1 ring-slate-200">
          {initial}
        </span>

        <span className="hidden min-w-0 flex-1 lg:block">
          <span className="block max-w-[125px] truncate text-sm font-semibold text-slate-800">
            {username}
          </span>
        </span>

        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 group-hover:text-slate-600 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-[48px] z-50 w-[300px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10"
          role="menu"
        >
          <div className="bg-gradient-to-br from-slate-50 to-white px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-sm font-semibold text-white shadow-sm">
                {initial}
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-900">{username}</div>
                <div className="truncate text-xs text-slate-500">{email}</div>
              </div>
            </div>

            <div className="mt-3 inline-flex items-center rounded-full border border-cyan-100 bg-cyan-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-cyan-700">
              {roleLabel}
            </div>
          </div>

          <div className="border-t border-slate-100 p-2">
            <button
              type="button"
              className="mt-0 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50 hover:text-slate-900"
              onClick={handleProfileSettings}
              role="menuitem"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                <UserRound className="h-4 w-4" />
              </span>
              Profile Settings
            </button>

            <button
              type="button"
              className="mt-0 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50 hover:text-slate-900"
              onClick={handleSupportSettings}
              role="menuitem"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                <CircleHelp className="h-4 w-4" />
              </span>
              Help & Support
            </button>

            <div className="my-2 h-px bg-slate-100" />

            <button
              type="button"
              className="mt-0 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-rose-600 transition hover:bg-rose-50"
              onClick={handleLogout}
              role="menuitem"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50 text-rose-500">
                <LogOut className="h-4 w-4" />
              </span>
              Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
